/* SafeImg — image element with automatic fallback chain.
 *
 * Pass `src` plus optional `fallbacks` (array of URLs to try in order)
 * and an optional `placeholder` (final brutalist redacted block). If any
 * URL in the chain 404s or errors out, the component automatically
 * advances to the next one. After exhausting all fallbacks it renders
 * the placeholder so the user never sees a broken-image icon.
 *
 * This is the user-facing version of the "nothing stays broken" rule:
 * users will NEVER see a permanent error symbol on their page — there's
 * always either a real image, a fallback URL, or a clean redacted block.
 */
import React, { useEffect, useMemo, useState } from "react";

export default function SafeImg({
  src,
  fallbacks = [],
  alt = "",
  className = "",
  placeholderLabel = "▒▒",
  onLoad,
  ...rest
}) {
  // Build the chain: primary src first, then any fallbacks.
  const chain = useMemo(() => {
    const list = [src, ...fallbacks].filter(Boolean);
    // Dedupe while preserving order
    return Array.from(new Set(list));
  }, [src, fallbacks]);

  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);

  // If the chain changes (e.g., parent passes a new image), restart.
  useEffect(() => {
    setIdx(0);
    setFailed(false);
  }, [chain.join("|")]);

  if (failed || chain.length === 0) {
    return (
      <div
        data-testid="safe-img-placeholder"
        className={`flex items-center justify-center bg-[#0a0a0a] border border-white/10 text-white/30 font-mono text-[10px] tracking-widest ${className}`}
        {...rest}
      >
        {placeholderLabel}
      </div>
    );
  }

  return (
    <img
      src={chain[idx]}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
      onError={() => {
        if (idx + 1 < chain.length) {
          setIdx(idx + 1);
        } else {
          setFailed(true);
        }
      }}
      {...rest}
    />
  );
}
